import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveServices, createConversationWithService } from "@/hooks/useServices";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ServiceCard } from "@/components/ServiceCard";
import BottomNav from "@/components/BottomNav";

export default function NewConversationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: services, isLoading, error } = useActiveServices();
  const [creating, setCreating] = useState<string | null>(null);

  const handleSelectService = async (serviceId: string) => {
    if (!user || creating) return;

    setCreating(serviceId);
    try {
      const conversation = await createConversationWithService(user.id, serviceId);
      navigate(`/chat/${conversation.id}`);
    } catch (err) {
      console.error("Failed to create conversation:", err);
      toast.error("Impossible de créer la conversation");
      setCreating(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-20 bg-background">
      {/* Header */}
      <header 
        className="px-4 sm:px-6 pt-6 sm:pt-8 pb-4"
        style={{ paddingTop: 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1.5rem))' }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/chat")}
              className="shrink-0 h-9 w-9"
            >
              <ArrowLeft size={18} />
            </Button>
            <h1 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
              Nouvelle demande
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground ml-11 sm:ml-12">
            Sélectionnez un service pour démarrer
          </p>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Erreur de chargement des services</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Réessayer
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {services?.map((service) => (
                <ServiceCard
                  key={service.id}
                  name={service.name}
                  description={service.description}
                  bgImageUrl={service.bg_image_url}
                  isLoading={creating === service.id}
                  disabled={!!creating}
                  onClick={() => handleSelectService(service.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
